import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { scansApi } from '../api/scans.ts';
import { billingApi } from '../api/billing.ts';
import { accountApi, type DeleteImpact } from '../api/account.ts';
import { useAuthStore } from '../state/authStore.ts';
import { ApiError } from '../api/client.ts';
import { useSeo } from '../hooks/useSeo.ts';

const PURCHASE_KINDS = new Set(['Purchase', 'SubscriptionGrant', 'SignupBonus']);

export default function ProfilePage() {
  useSeo({
    title: 'Profile',
    noindex: true,
  });

  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const setUser = useAuthStore(s => s.setUser);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [exportError, setExportError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteImpact, setDeleteImpact] = useState<DeleteImpact | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const exportData = useMutation({
    mutationFn: () => accountApi.downloadExport(),
    onError: () => setExportError("Couldn't generate your export. Try again or email support@eaaproof.com."),
  });

  const deleteAccount = useMutation({
    mutationFn: () => accountApi.deleteAccount(),
    onSuccess: () => {
      setUser(null);
      navigate('/?deleted=1');
    },
    onError: () => setDeleteError("Couldn't delete your account. Email support@eaaproof.com and we'll handle it within 30 days."),
  });

  const { data: summary } = useQuery({
    queryKey: ['billing', 'summary'],
    queryFn: billingApi.summary,
    enabled: Boolean(user),
  });

  const portalMutation = useMutation({
    mutationFn: billingApi.portal,
    onSuccess: result => { window.location.href = result.url; },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setPortalError(err.detail || err.title);
      } else {
        setPortalError('Could not open the Stripe portal. Try again in a moment.');
      }
    },
  });

  const { data: scansResponse, isLoading: scansLoading } = useQuery({
    queryKey: ['scans', 'list'],
    queryFn: () => scansApi.list(50, 0),
    enabled: Boolean(user),
  });

  if (!user) {
    return (
      <div className="page">
        <h1>Profile</h1>
        <p className="muted">You need to sign in to view your profile.</p>
        <Link to="/sign-in" className="cta">Sign in</Link>
      </div>
    );
  }

  // Filter to purchase-type transactions only — scan charges aren't "purchases" for this view.
  const purchases = (summary?.recentTransactions ?? []).filter(t => PURCHASE_KINDS.has(t.kind));
  const scans = scansResponse?.items ?? [];

  return (
    <div className="page profile">
      <header className="profile-header">
        <div>
          <h1>{user.displayName ?? user.email}</h1>
          <p className="muted">{user.email}</p>
        </div>
        <div className="profile-balance">
          <span className="profile-balance-value">{user.creditBalance}</span>
          <span className="profile-balance-label">credits</span>
        </div>
      </header>

      <section className="profile-section">
        <div className="profile-section-head">
          <h2>Purchase history</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {summary?.hasStripeCustomer && (
              <button
                type="button"
                className="cta cta-inline secondary"
                onClick={() => { setPortalError(null); portalMutation.mutate(); }}
                disabled={portalMutation.isPending}
              >
                {portalMutation.isPending ? 'Opening…' : 'Manage subscription'}
              </button>
            )}
            <Link to="/pricing" className="cta cta-inline">Buy more</Link>
          </div>
        </div>
        {portalError && <p className="error" role="alert">{portalError}</p>}
        {purchases.length === 0 ? (
          <p className="muted">No purchases yet. Buy a credit pack to run your first scan.</p>
        ) : (
          <table className="profile-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Reason</th>
                <th className="num">Credits</th>
                <th className="num">Balance after</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map(t => (
                <tr key={t.id}>
                  <td>{new Date(t.createdAt).toLocaleString()}</td>
                  <td>{prettyKind(t.kind)}</td>
                  <td>{t.reason}</td>
                  <td className="num">+{t.delta}</td>
                  <td className="num">{t.balanceAfter}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="profile-section">
        <div className="profile-section-head">
          <h2>Past scans</h2>
          <span className="muted small">{scansResponse?.total ?? 0} total</span>
        </div>

        {scansLoading ? (
          <p className="muted">Loading scans…</p>
        ) : scans.length === 0 ? (
          <p className="muted">No scans yet. Go scan a URL on the home page.</p>
        ) : (
          <table className="profile-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Site</th>
                <th>Status</th>
                <th>Duration</th>
                <th className="num">Score</th>
                <th className="num">Issues</th>
                <th>Severity</th>
                <th>Downloads</th>
              </tr>
            </thead>
            <tbody>
              {scans.map(s => {
                const totalIssues = s.criticalCount + s.seriousCount + s.moderateCount + s.minorCount;
                const completed = s.status === 'Completed';
                return (
                  <tr key={s.id}>
                    <td>
                      <div>{new Date(s.createdAt).toLocaleDateString()}</div>
                      <div className="muted small">{new Date(s.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    <td>
                      <Link to={`/scan/${s.id}`} className="scan-link">
                        {s.pageTitle ?? friendlyUrl(s.targetUrl)}
                      </Link>
                      <div className="muted small">{friendlyUrl(s.targetUrl)}</div>
                    </td>
                    <td>
                      <span className={`status-tag status-${s.status.toLowerCase()}`}>{s.status}</span>
                    </td>
                    <td className="muted small">{formatDuration(s.createdAt, s.completedAt)}</td>
                    <td className="num">
                      {completed ? <strong>{s.complianceScore}</strong> : '—'}
                    </td>
                    <td className="num">
                      {completed ? totalIssues : '—'}
                    </td>
                    <td>
                      {completed ? (
                        <SeverityChips s={s} />
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {s.hasReport && (
                        <a
                          href={scansApi.reportPdfUrl(s.id)}
                          download
                          className="download-link"
                          title="Download PDF report"
                        >
                          PDF
                        </a>
                      )}
                      {s.hasStatement && (
                        <a
                          href={scansApi.statementHtmlUrl(s.id)}
                          download
                          className="download-link"
                          title="Download accessibility statement"
                        >
                          HTML
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <ChangePasswordPanel />

      <section className="profile-section">
        <div className="profile-section-head">
          <h2>Your data</h2>
        </div>
        <p className="muted">
          Under GDPR you have the right to access and erase the personal data we hold about you.
          See the <Link to="/privacy">privacy policy</Link> for details.
        </p>

        <div className="data-actions">
          <button
            type="button"
            className="cta cta-inline secondary"
            onClick={() => { setExportError(null); exportData.mutate(); }}
            disabled={exportData.isPending}
          >
            {exportData.isPending ? 'Preparing…' : 'Export my data (JSON)'}
          </button>
          {!showDeleteConfirm ? (
            <button
              type="button"
              className="cta cta-inline cta-danger"
              onClick={async () => {
                setDeleteError(null);
                setShowDeleteConfirm(true);
                setImpactLoading(true);
                try {
                  const impact = await accountApi.deleteImpact();
                  setDeleteImpact(impact);
                } catch {
                  // Failure here just means we can't preview active subs — the delete itself
                  // still cancels them server-side. Don't block the user.
                  setDeleteImpact({ activeSubscriptions: [] });
                } finally {
                  setImpactLoading(false);
                }
              }}
            >
              Delete my account
            </button>
          ) : null}
        </div>

        {exportError && <p className="error" role="alert">{exportError}</p>}

        {showDeleteConfirm && (
          <div className="delete-confirm">
            <p>
              <strong>This is irreversible.</strong> We will permanently delete your account,
              all your scans, generated reports, and credit history. Stripe billing records
              are retained for 10 years as required by EU VAT law.
            </p>

            {impactLoading ? (
              <p className="muted small">Checking what will be cancelled…</p>
            ) : deleteImpact && deleteImpact.activeSubscriptions.length > 0 ? (
              <div className="delete-impact">
                <p><strong>You'll also be cancelling these active subscriptions:</strong></p>
                <ul>
                  {deleteImpact.activeSubscriptions.map(sub => (
                    <li key={sub.subscriptionId}>
                      <strong>{sub.displayName ?? sub.planCode ?? 'Subscription'}</strong>
                      {sub.currentPeriodEnd && (
                        <span className="muted small">
                          {' '}— billing through {new Date(sub.currentPeriodEnd).toLocaleDateString()}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <p>
              To confirm, type <code>DELETE</code> below:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              aria-label="Type DELETE to confirm"
              className="delete-confirm-input"
            />
            {deleteError && <p className="error" role="alert">{deleteError}</p>}
            <div className="data-actions">
              <button
                type="button"
                className="cta cta-inline secondary"
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeleteError(null); }}
                disabled={deleteAccount.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cta cta-inline cta-danger"
                onClick={() => deleteAccount.mutate()}
                disabled={deleteConfirmText !== 'DELETE' || deleteAccount.isPending}
              >
                {deleteAccount.isPending ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function SeverityChips({ s }: { s: { criticalCount: number; seriousCount: number; moderateCount: number; minorCount: number } }) {
  return (
    <div className="severity-chips">
      {s.criticalCount > 0 && <span className="chip chip-critical">{s.criticalCount} Critical</span>}
      {s.seriousCount > 0 && <span className="chip chip-serious">{s.seriousCount} Serious</span>}
      {s.moderateCount > 0 && <span className="chip chip-moderate">{s.moderateCount} Moderate</span>}
      {s.minorCount > 0 && <span className="chip chip-minor">{s.minorCount} Minor</span>}
      {s.criticalCount + s.seriousCount + s.moderateCount + s.minorCount === 0 && (
        <span className="muted small">No issues</span>
      )}
    </div>
  );
}

function prettyKind(kind: string): string {
  switch (kind) {
    case 'Purchase': return 'One-time purchase';
    case 'SubscriptionGrant': return 'Subscription renewal';
    case 'SignupBonus': return 'Welcome bonus';
    case 'ScanCharge': return 'Scan';
    case 'Refund': return 'Refund';
    case 'AdminAdjustment': return 'Adjustment';
    default: return kind;
  }
}

function ChangePasswordPanel() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: ({ current, next }: { current: string; next: string }) =>
      accountApi.changePassword(current, next),
    onSuccess: () => {
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
      setTimeout(() => setDone(false), 4000);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else setError('Could not change password. Try again in a moment.');
    },
  });

  return (
    <section className="profile-section">
      <div className="profile-section-head">
        <h2>Password</h2>
      </div>
      <p className="muted">
        Change your password without losing access. Every other signed-in device will be signed
        out within ~30 seconds.
      </p>
      <form
        className="auth-form change-password-form"
        onSubmit={e => {
          e.preventDefault();
          setError(null);
          if (next.length < 8) { setError('New password must be at least 8 characters.'); return; }
          if (next !== confirm) { setError('New passwords do not match.'); return; }
          mutation.mutate({ current, next });
        }}
      >
        <label>
          <span>Current password</span>
          <input type="password" autoComplete="current-password" required value={current}
                 onChange={e => setCurrent(e.target.value)} minLength={8} maxLength={128} />
        </label>
        <label>
          <span>New password</span>
          <input type="password" autoComplete="new-password" required value={next}
                 onChange={e => setNext(e.target.value)} minLength={8} maxLength={128} />
        </label>
        <label>
          <span>Confirm new password</span>
          <input type="password" autoComplete="new-password" required value={confirm}
                 onChange={e => setConfirm(e.target.value)} minLength={8} maxLength={128} />
        </label>
        {error && <p className="error" role="alert">{error}</p>}
        {done && <p className="muted small" role="status">Password updated. Other devices signed out.</p>}
        <button type="submit" className="cta cta-inline" disabled={mutation.isPending}>
          {mutation.isPending ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </section>
  );
}

function friendlyUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.host + (u.pathname === '/' ? '' : u.pathname);
  } catch {
    return url;
  }
}

function formatDuration(createdAt: string, completedAt: string | null): string {
  if (!completedAt) return '—';
  const ms = new Date(completedAt).getTime() - new Date(createdAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return rem === 0 ? `${minutes}m` : `${minutes}m ${rem}s`;
}
